// src/pages/PublicCatalog/components/ProductDetails.js
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    Box,
    Button,
    TextField,
    Chip,
    useTheme,
    useMediaQuery
} from '@material-ui/core';
import {
    Close as CloseIcon,
    Add as AddIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon
} from '@material-ui/icons';
import { getBackendUrl } from "../../../config";

const useStyles = makeStyles((theme) => ({
    detailsDialog: {
        '& .MuiDialog-paper': {
            maxWidth: 800,
            width: '100%',
            margin: theme.spacing(2),
        },
    },
    detailsImage: {
        width: '100%',
        height: 400,
        objectFit: 'contain',
        backgroundColor: theme.palette.grey[100],
        [theme.breakpoints.down('sm')]: {
            height: 250,
        },
    },
    imageContainer: {
        position: 'relative',
    },
    navigationArrow: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
        },
    },
    leftArrow: {
        left: theme.spacing(1),
    },
    rightArrow: {
        right: theme.spacing(1),
    },
    detailsImageNav: {
        display: 'flex',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
        overflowX: 'auto',
        padding: theme.spacing(1),
        '&::-webkit-scrollbar': {
            height: 6,
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.grey[200],
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[400],
            borderRadius: 3,
        },
    },
    thumbnailImage: {
        width: 60,
        height: 60,
        objectFit: 'cover',
        cursor: 'pointer',
        border: `2px solid transparent`,
        borderRadius: theme.shape.borderRadius,
        '&.active': {
            borderColor: theme.palette.primary.main,
        },
    },
    price: {
        color: theme.palette.primary.main,
        fontWeight: 'bold',
        fontSize: '1.5rem',
        marginTop: theme.spacing(2),
    },
    description: {
        marginTop: theme.spacing(2),
        whiteSpace: 'pre-line',
    },
    attributes: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(1),
    },
    chip: {
        margin: theme.spacing(0.5),
    },
    addToCartSection: {
        padding: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    quantityInput: {
        width: 120,
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
}));

const ProductDetails = ({ open, onClose, product, onAddToCart }) => {
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [quantity, setQuantity] = useState(1);

    if (!product) return null;

    const handleAddToCart = () => {
        onAddToCart(product.id, quantity);
        setQuantity(1);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={classes.detailsDialog}
            fullScreen={fullScreen}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{product.name}</Typography>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {product.images && product.images.length > 0 && (
                    <>
                        <div className={classes.imageContainer}>
                            <img
                                src={`${getBackendUrl()}public/products/${product.images[currentImageIndex].imageUrl}`}
                                alt={product.name}
                                className={classes.detailsImage}
                            />
                            {product.images.length > 1 && (
                                <>
                                    <IconButton
                                        className={`${classes.navigationArrow} ${classes.leftArrow}`}
                                        onClick={() => setCurrentImageIndex(prev => Math.max(0, prev - 1))}
                                        disabled={currentImageIndex === 0}
                                    >
                                        <NavigateBeforeIcon />
                                    </IconButton>
                                    <IconButton
                                        className={`${classes.navigationArrow} ${classes.rightArrow}`}
                                        onClick={() => setCurrentImageIndex(prev => Math.min(product.images.length - 1, prev + 1))}
                                        disabled={currentImageIndex === product.images.length - 1}
                                    >
                                        <NavigateNextIcon />
                                    </IconButton>
                                </>
                            )}
                        </div>
                        {product.images.length > 1 && (
                            <div className={classes.detailsImageNav}>
                                {product.images.map((image, index) => (
                                    <img
                                        key={index}
                                        src={`${getBackendUrl()}public/products/${image.imageUrl}`}
                                        alt={`${product.name} ${index + 1}`}
                                        className={`${classes.thumbnailImage} ${currentImageIndex === index ? 'active' : ''}`}
                                        onClick={() => setCurrentImageIndex(index)}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}

                <Typography variant="h5" className={classes.price}>
                    {new Intl.NumberFormat('es-ES', {
                        style: 'currency',
                        currency: product.currency || 'USD'
                    }).format(product.price)}
                </Typography>

                {product.description && (
                    <Typography variant="body1" className={classes.description}>
                        {product.description}
                    </Typography>
                )}

                {product.attributes && product.attributes.length > 0 && (
                    <Box mt={3}>
                        <Typography variant="subtitle1" gutterBottom>
                            Características
                        </Typography>
                        <div className={classes.attributes}>
                            {product.attributes.map((attr, index) => (
                                <Chip
                                    key={index}
                                    label={`${attr.name}: ${attr.value}`}
                                    size="small"
                                    className={classes.chip}
                                />
                            ))}
                        </div>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <div className={classes.addToCartSection}>
                    <TextField
                        label="Cantidad"
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                        InputProps={{ inputProps: { min: 1 } }}
                        variant="outlined"
                        size="small"
                        className={classes.quantityInput}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddToCart}
                        startIcon={<AddIcon />}
                        fullWidth
                    >
                        Agregar al carrito
                        
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
};

export default ProductDetails;